
/**
 * Context Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { useContextStore } from '../../store/context.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
export class ContextIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch contexts and update store
     */
    async fetchContexts(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useContextStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            // With createBaseController, response has:
            // { success, data (array), pagination, uniqueValues }
            const { data, pagination, uniqueValues } = response;
            store.setContexts(data || []);

            // Map pagination from backend response payload
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                // Fallback if pagination missing
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create context
     */
    async createContext(data) {
        const store = useContextStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.create(data);

            toast.success(i18next.t('organizationalContext:messages.createSuccess', { defaultValue: 'Context created successfully.' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update context
     */
    async updateContext(id, data) {
        const store = useContextStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('organizationalContext:messages.updateSuccess', { defaultValue: 'Context updated successfully.' }));

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete context
     */
    async deleteContext(id) {
        const store = useContextStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.delete(id);

            toast.success(i18next.t('organizationalContext:messages.deleteSuccess', { defaultValue: 'Context deleted successfully.' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete contexts
     * @param {Array<string>} ids
     */
    async batchDeleteContext(ids) {
        const store = useContextStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.bulkDelete(ids);

            toast.success(i18next.t('organizationalContext:messages.bulkDeleteSuccess', { count: ids.length, defaultValue: `${ids.length} contexts deleted successfully.` }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Context not found',
        });
    }
}
